import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import Modal from "@components/Modal/Modal";
import { iPreset, useGapReportData } from "@apiHooks/useGapReportData";
import Button from "@components/new/Button/Button";
import './GapAnalysisModal.styles.scss';
import NestedTiles from "@components/new/Tiles/NestedTiles";
import cn from "classnames";
import Table from "@components/new/Table/Table";
import { iTableConfigItem } from "@components/new/Table/Table.helpers";
import { getFormattedDate } from "@helpers";
import ChainsCompareSearch from "./ChainsCompareSearch/ChaisCompareSearch";
import { ID } from "@models";
import { iOption } from "@components/new/Autocomplete/Autocomplete.helpers";
import Loader from "@components/new/Loader/Loader";
import { CustomBaseScrollbars } from "@components/CustomScrollbarsVirtualList/CustomScrollbarsVirtualList";

interface iProps {
    close: () => void;
    chainId: number;
    chainName: string;
}

interface iStep {
    content: ReactNode;
    footer: ReactNode;
}

interface iSteps {
    start: iStep;
    choosePreset: iStep;
    createPreset: iStep;
    chooseReport: iStep;
    chooseCustomReport: iStep;
}

const GapAnalysisModal: FC<iProps> = (
    {
        close,
        chainId,
        chainName,
    }
) => {
    const {
        isLoading,
        isReportLoading,
        data,
        downloadReport,
        downloadSimpleReport,
    } = useGapReportData(chainId);

    const [currentStep, setCurrentStep] = useState<keyof iSteps>('start');
    const [selectedChainsIds, setSelectedChainsIds] = useState<ID[]>([]);
    const [selectedPresetName, setSelectedPresetName] = useState<string>('');

    const interactiveListsAsTilesItems = useMemo(() => {
        return data.interactiveLists.map((lvl1) => ({
            id: lvl1.id,
            title: lvl1.name,
            subItems: lvl1.childReports.map((lvl2) => ({
                id: lvl2.id,
                title: lvl2.name,
                subItems: lvl2.childReports.map((lvl3) => ({
                    id: lvl3.id,
                    title: lvl3.name,
                    subItems: [],
                }))
            }))
        }))
    }, [data.interactiveLists]);

    const handlePresetClick = (preset: iPreset) => {
        setSelectedChainsIds(preset.chains.map(i => i.fireflyChainId));
        setSelectedPresetName(preset.name);
        setCurrentStep("chooseReport");
    };

    const handleReportSelect = (id: number) => {
        downloadReport(selectedChainsIds, selectedPresetName, id).then(close).catch(() => undefined);
    }

    const handleCustomReportSelect = useCallback((id: number) => {
        downloadReport(selectedChainsIds, selectedPresetName, id, true).then(close).catch(() => undefined);
    }, [downloadReport, selectedChainsIds, selectedPresetName, close]);

    const handleSimpleReportClick = () => {
        downloadSimpleReport(selectedChainsIds, selectedPresetName).then(close).catch(() => undefined);
    }

    useEffect(() => {
        if (currentStep === 'choosePreset' || currentStep === 'createPreset') {
            setSelectedChainsIds([]);
            setSelectedPresetName('');
        }
    }, [currentStep]);

    const savedQueriesTableConfig: iTableConfigItem[] = useMemo(() => ([
        {
            title: 'Report name',
            Value: ({ index }) => (
                <div
                    className="GapAnalysisModal__link"
                    onClick={() => handleCustomReportSelect(data.savedQueries[index].query.id)}
                >
                    {data.savedQueries[index].query.title}
                </div>
            ),
        },
        {
            title: 'Date',
            width: 100,
            Value: ({ index }) => <>
                {getFormattedDate(data.savedQueries[index].query.created, 'MM/DD/YYYY')}
            </>
        },
        {
            title: '#  of words',
            width: 100,
            Value: ({ index }) => (
                <div style={{ textAlign: 'center', width: '100%' }}>
                    {data.savedQueries[index].words.length}
                </div>
            ),
        },
    ]), [data.savedQueries, handleCustomReportSelect]);


    const steps: iSteps = {
        start: {
            content: (
                <div className="GapAnalysisModal__start">
                    <div className="GapAnalysisModal__heading2">
                        Create an instant MENU GAP ANALYSIS <br/>that benchmarks the&nbsp;
                        <b className="GapAnalysisModal__link GapAnalysisModal__link--text">{chainName}</b>
                        &nbsp;competitors.
                    </div>
                    <br/>
                    <img src={`${process.env.PUBLIC_URL}/gaps.png`} alt="Menu gap analysis preview"/>
                </div>
            ),
            footer: (
                <div className="GapAnalysisModal__section GapAnalysisModal__section--center">
                    <div className="GapAnalysisModal__button">
                        <Button
                            modifiers={["secondary"]}
                            onClick={close}
                        >
                            cancel
                        </Button>
                    </div>
                    <div className="GapAnalysisModal__button">
                        <Button
                            modifiers={["primary"]}
                            onClick={() => setCurrentStep("choosePreset")}
                            isLoading={isLoading || isReportLoading}
                        >
                            Let's go
                        </Button>
                    </div>
                </div>
            ),
        },
        choosePreset: {
            content: (
                <>
                    <div className="GapAnalysisModal__heading2 mb">
                        compare {chainName} to
                    </div>
                    <div className="GapAnalysisModal__columns">
                        {data.presets.map((presetGroup) => (
                            <div
                                key={presetGroup.presetClass}
                                className="GapAnalysisModal__column"
                            >
                                <div className="GapAnalysisModal__heading3 mb">
                                    {presetGroup.presetName}
                                </div>
                                <div className="GapAnalysisModal__list">
                                    <CustomBaseScrollbars>
                                        {presetGroup.preset.map((preset) => {
                                            const className = cn(
                                                'GapAnalysisModal__link',
                                                preset.isCustom && 'GapAnalysisModal__link--accent',
                                            );
                                            return (
                                                <div
                                                    key={preset.id}
                                                    className={className}
                                                    onClick={() => handlePresetClick(preset)}
                                                >
                                                    {preset.name} ({preset.chains.length})
                                                </div>
                                            );
                                        })}
                                    </CustomBaseScrollbars>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            ),
            footer: (
                <div className="GapAnalysisModal__section GapAnalysisModal__section--justify">
                    <div className="GapAnalysisModal__button">
                        <Button
                            modifiers={["secondary"]}
                            onClick={() => setCurrentStep("start")}
                        >
                            {`<< back`}
                        </Button>
                    </div>
                    <div className="GapAnalysisModal__button">
                        <p>
                            You can also&nbsp;
                            <span
                                className="GapAnalysisModal__link"
                                onClick={() => setCurrentStep("createPreset")}
                            >
                                build
                            </span>
                            &nbsp;your own competitive set
                        </p>
                    </div>
                </div>
            ),
        },
        createPreset: {
            content: (
                <>
                    <div className="GapAnalysisModal__heading2">
                        compare {chainName} to
                    </div>
                    <div className="GapAnalysisModal__form">
                        <div className="GapAnalysisModal__autocomplete">
                            <ChainsCompareSearch
                                chainId={chainId}
                                setSelected={(options: iOption[]) => {
                                    setSelectedChainsIds(options.map((o: iOption) => o.id));
                                    setSelectedPresetName('Custom');
                                }}
                            />
                        </div>
                    </div>
                </>
            ),
            footer: (
                <div className="GapAnalysisModal__section">
                    <div className="GapAnalysisModal__button">
                        <Button
                            modifiers={["secondary"]}
                            onClick={() => setCurrentStep("choosePreset")}
                        >
                            {`<< back`}
                        </Button>
                    </div>
                    <div className="GapAnalysisModal__button">
                        <Button
                            modifiers={["primary"]}
                            isDisabled={selectedChainsIds.length === 0}
                            onClick={() => setCurrentStep("chooseReport")}
                        >
                            next
                        </Button>
                    </div>
                </div>
            ),
        },
        chooseReport: {
            content: (
                <>
                    <div className="GapAnalysisModal__heading2 mb">
                        GREAT! NOW JUST SELECT A REPORT AND YOU'RE GOOD TO GO.
                    </div>
                    <div className="GapAnalysisModal__section GapAnalysisModal__justify">
                        <div className="GapAnalysisModal__heading3 mb">
                            Select a category report:
                        </div>
                    </div>
                    <div className="GapAnalysisModal__interactive-lists">
                        <NestedTiles
                            data={interactiveListsAsTilesItems}
                            handleLastNestedClick={handleReportSelect}
                        />
                    </div>
                </>
            ),
            footer: (
                <div className="GapAnalysisModal__section GapAnalysisModal__section--justify">
                    <div className="GapAnalysisModal__button">
                        <Button
                            modifiers={["secondary"]}
                            onClick={() => setCurrentStep("choosePreset")}
                        >
                            {`<< back`}
                        </Button>
                    </div>
                    <div className="GapAnalysisModal__section-line">
                        <div className="GapAnalysisModal__heading2">
                            or&nbsp;&nbsp;&nbsp;&nbsp;
                        </div>
                        <button
                            className="GapAnalysisModal__high-button"
                            onClick={() => setCurrentStep("chooseCustomReport")}
                        >
                            use one of your saved<br/>MenuTrends BYO reports
                        </button>
                        <button
                            className="GapAnalysisModal__high-button"
                            onClick={handleSimpleReportClick}
                        >
                            view a simple report<br/>by major menu part
                        </button>
                    </div>
                </div>
            ),
        },
        chooseCustomReport: {
            content: (
                <>
                    <div className="GapAnalysisModal__heading2">
                        GREAT! NOW JUST SELECT A REPORT AND YOU'RE GOOD TO GO.
                    </div>
                    <div className="GapAnalysisModal__saved-queries-table">
                        <Table
                            config={savedQueriesTableConfig}
                            rowsCount={data.savedQueries.length}
                        />
                    </div>
                </>
            ),
            footer: (
                <div className="GapAnalysisModal__section GapAnalysisModal__section--justify">
                    <div className="GapAnalysisModal__button">
                        <Button
                            modifiers={["secondary"]}
                            onClick={() => setCurrentStep("chooseReport")}
                        >
                            {`<< back`}
                        </Button>
                    </div>
                </div>
            )
        }
    };

    return (
        <Modal isOpened={true} close={close} className="ChainPage__modal">
            <div className="GapAnalysisModal">
                {isReportLoading && <Loader/>}
                <div className="GapAnalysisModal__header">
                    <div className="GapAnalysisModal__heading1">
                        MENU GAPS
                    </div>
                </div>
                <div className="GapAnalysisModal__body">
                    {steps[currentStep].content}
                </div>
                <div className="GapAnalysisModal__footer">
                    {steps[currentStep].footer}
                </div>
            </div>
        </Modal>
    );
}
export default GapAnalysisModal;
